import React from 'react';
import { Commission, Tier } from '../types/commission';
import { Calendar, Briefcase, AlertTriangle, TrendingDown, TrendingUp, Clock, Award } from 'lucide-react';
import { motion } from 'framer-motion';
interface CommissionCardProps {
  commission: Commission;
}
function getTierColor(tier: Tier): {
  bg: string;
  text: string;
  border: string;
} {
  const colors = {
    5: {
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      border: 'border-amber-200'
    },
    4: {
      bg: 'bg-purple-100',
      text: 'text-purple-700',
      border: 'border-purple-200'
    },
    3: {
      bg: 'bg-indigo-100',
      text: 'text-indigo-700',
      border: 'border-indigo-200'
    },
    2: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-200'
    },
    1: {
      bg: 'bg-slate-100',
      text: 'text-slate-700',
      border: 'border-slate-200'
    }
  };
  return colors[tier];
}
export function CommissionCard({
  commission
}: CommissionCardProps) {
  const hasTierDowngrade = commission.lateCount > 3 && commission.originalTier && commission.currentTier && commission.lossAmount;
  const hasTierUpgrade = commission.lateCount <= 1 && commission.originalTier && commission.currentTier && commission.gainAmount;
  const hasLateWarning = commission.lateCount >= 2 && commission.lateCount <= 3;
  return <motion.div layout initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} whileHover={{
    y: -4,
    transition: {
      duration: 0.2
    }
  }} className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col h-full" data-id="element-1">
      {/* Card Header */}
      <div className="p-5 border-b border-slate-100 bg-slate-50/50" data-id="element-2">
        <div className="flex justify-between items-start mb-3" data-id="element-3">
          <div className="flex items-center gap-3" data-id="element-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm" data-id="element-5">
              {commission.userName.split(' ').map(n => n[0]).join('')}
            </div>
            <div data-id="element-6">
              <h3 className="font-bold text-slate-900" data-id="element-7">
                {commission.userName}
              </h3>
              <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5" data-id="element-8">
                <Briefcase size={12} data-id="element-9" />
                <span data-id="element-10">{commission.userProfile}</span>
              </div>
            </div>
          </div>
          <div className="text-right" data-id="element-11">
            <div className="text-lg font-bold text-slate-900" data-id="element-12">
              $
              {commission.total.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
            </div>
            <div className="text-xs text-slate-500 font-medium uppercase tracking-wide" data-id="element-13">
              Total Comm.
            </div>
          </div>
        </div>

        {/* Tier Upgrade Banner (≤1 late) */}
        {hasTierUpgrade && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-id="element-14">
            <div className="flex items-start gap-2" data-id="element-15">
              <Award size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" data-id="element-16" />
              <div className="flex-1" data-id="element-17">
                <div className="flex items-center gap-2 mb-1" data-id="element-18">
                  <span className="text-xs font-semibold text-emerald-700" data-id="element-19">
                    Tier Upgrade Applied
                  </span>
                  <div className="flex items-center gap-1 text-xs" data-id="element-20">
                    <span className={`px-1.5 py-0.5 rounded ${getTierColor(commission.originalTier).bg} ${getTierColor(commission.originalTier).text} font-medium`} data-id="element-21">
                      Tier {commission.originalTier}
                    </span>
                    <TrendingUp size={12} className="text-emerald-500" data-id="element-22" />
                    <span className={`px-1.5 py-0.5 rounded ${getTierColor(commission.currentTier).bg} ${getTierColor(commission.currentTier).text} font-medium`} data-id="element-23">
                      Tier {commission.currentTier}
                    </span>
                  </div>
                </div>
                <div className="text-xs text-emerald-600 flex items-center gap-1" data-id="element-24">
                  <Clock size={12} data-id="element-25" />
                  <span data-id="element-26">
                    <span className="font-medium" data-id="element-27">
                      {commission.lateCount === 0 ? 'Perfect attendance' : '1 late arrival'}{' '}
                      this month
                    </span>
                    <span className="mx-1" data-id="element-28">→</span>
                    <span className="font-bold text-emerald-700" data-id="element-29">
                      +$
                      {commission.gainAmount?.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </span>{' '}
                    commission bonus
                  </span>
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Tier Downgrade Banner (>3 lates) */}
        {hasTierDowngrade && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-30">
            <div className="flex items-start gap-2" data-id="element-31">
              <AlertTriangle size={16} className="text-red-600 mt-0.5 flex-shrink-0" data-id="element-32" />
              <div className="flex-1" data-id="element-33">
                <div className="flex items-center gap-2 mb-1" data-id="element-34">
                  <span className="text-xs font-semibold text-red-700" data-id="element-35">
                    Tier Downgrade Applied
                  </span>
                  <div className="flex items-center gap-1 text-xs" data-id="element-36">
                    <span className={`px-1.5 py-0.5 rounded ${getTierColor(commission.originalTier).bg} ${getTierColor(commission.originalTier).text} font-medium`} data-id="element-37">
                      Tier {commission.originalTier}
                    </span>
                    <TrendingDown size={12} className="text-red-500" data-id="element-38" />
                    <span className={`px-1.5 py-0.5 rounded ${getTierColor(commission.currentTier).bg} ${getTierColor(commission.currentTier).text} font-medium`} data-id="element-39">
                      Tier {commission.currentTier}
                    </span>
                  </div>
                </div>
                <div className="text-xs text-red-600 flex items-center gap-1" data-id="element-40">
                  <Clock size={12} data-id="element-41" />
                  <span data-id="element-42">
                    <span className="font-medium" data-id="element-43">
                      Late to work {commission.lateCount} times
                    </span>{' '}
                    this month
                    <span className="mx-1" data-id="element-44">→</span>
                    <span className="font-bold text-red-700" data-id="element-45">
                      -$
                      {commission.lossAmount?.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </span>{' '}
                    commission loss
                  </span>
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Late Warning (2-3 lates) - No tier change yet */}
        {hasLateWarning && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2" data-id="element-46">
            <Clock size={14} className="text-amber-600 flex-shrink-0" data-id="element-47" />
            <span className="text-xs text-amber-700" data-id="element-48">
              <span className="font-semibold" data-id="element-49">
                Late to work {commission.lateCount} times
              </span>
              {commission.lateCount === 3 ? ' - One more late arrival triggers tier downgrade!' : ` - ${3 - commission.lateCount} more late arrival triggers tier downgrade`}
            </span>
          </motion.div>}
      </div>

      {/* Card Body - Loans List */}
      <div className="flex-1 p-0 overflow-y-auto max-h-[240px] scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent" data-id="element-50">
        {commission.loans.length > 0 ? <div className="divide-y divide-slate-100" data-id="element-51">
            {commission.loans.map(loan => {
          const tierColors = getTierColor(loan.tier);
          return <div key={loan.loanId} className="px-5 py-3 hover:bg-slate-50 transition-colors group" data-id="element-52">
                  <div className="flex justify-between items-center mb-1" data-id="element-53">
                    <span className="text-sm font-medium text-slate-700" data-id="element-54">
                      {loan.loanId}
                    </span>
                    <span className="text-sm font-semibold text-slate-900" data-id="element-55">
                      $
                      {loan.commission.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-slate-500" data-id="element-56">
                    <div className="flex items-center gap-2" data-id="element-57">
                      <span data-id="element-58">{loan.date}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-59" />
                      <span className={`px-1.5 py-0.5 rounded border font-medium ${tierColors.bg} ${tierColors.text} ${tierColors.border}`} data-id="element-60">
                        Tier {loan.tier}
                      </span>
                    </div>
                    <span data-id="element-61">${(loan.amount / 1000).toFixed(0)}k Loan</span>
                  </div>
                </div>;
        })}
          </div> : <div className="h-32 flex flex-col items-center justify-center text-slate-400 text-sm italic" data-id="element-62">
            No loans for this period
          </div>}
      </div>

      {/* Card Footer */}
      <div className="p-4 border-t border-slate-100 bg-slate-50/30 flex justify-between items-center text-xs text-slate-500 font-medium" data-id="element-63">
        <div className="flex items-center gap-1.5" data-id="element-64">
          <Calendar size={14} data-id="element-65" />
          <span data-id="element-66">{commission.payPeriod}</span>
        </div>
        <div className="bg-white px-2.5 py-1 rounded-full border border-slate-200 shadow-sm" data-id="element-67">
          {commission.units} Units
        </div>
      </div>
    </motion.div>;
}